import { createApp } from 'vue';
import { createPinia } from 'pinia';
import AppContainer from './components/AppContainer.vue';
import router from './router';
import { useEventsListStore } from './stores/events-list-store';
import { initEventListCalendarBlocks } from '../blocks/event-list-calendar';
import { initFeaturedTrayLinks } from '../blocks/featured-trays-links';

// Import Main css file (so Vite recognises it needs to build the CSS too!)
import '../../css/main.css';

// Import Javascript files for Gutenberg blocks so Vite recognises them
import '../blocks/faq-accordion.js';
import '../blocks/featured-trays-info.js';
import '../blocks/livestream-instructions-events.js';

const windowAppData =
  typeof window !== 'undefined' && window.gsdNowAppData ? window.gsdNowAppData : {};

const props = {
  appConfiguration: windowAppData.appConfiguration ?? null,
  userData: windowAppData.userData ?? null,
};

/**
 * Attach the Vue devtools client when the theme is running with it enabled.
 *
 * Imported on demand rather than at the top of the module so the client is split into its
 * own chunk and never downloaded by real users.
 */
async function connectDevtools() {
  if (
    typeof window === 'undefined' ||
    !Object.hasOwn(window, 'CITE_THEME_DEBUG') ||
    window.CITE_THEME_DEBUG !== true
  ) {
    return;
  }

  const { devtools } = await import('@vue/devtools');

  devtools.connect();
}

/**
 * Boot the SPA.
 *
 * Async so devtools can be connected before the first app is created.
 * The `await` is deliberately inside a function rather than at the top level.
 */
async function boot() {
  await connectDevtools();

  const pinia = createPinia();

  const app = createApp(AppContainer, props);

  app.use(pinia);

  if (windowAppData.eventView?.event) {
    useEventsListStore().setEventFromHydration(windowAppData.eventView.event, {
      matchedSlug: windowAppData.eventView.matchedSlug,
      needsDetailRefresh: windowAppData.eventView.needsDetailRefresh === true,
    });
  }

  app.use(router);

  await router.isReady();

  app.mount('#content-container');

  // The block islands are separate apps, so they need the same pinia instance handed to
  // them explicitly — that is what lets a block seed the stores the main app reads from.
  initEventListCalendarBlocks(router, pinia);
  initFeaturedTrayLinks(router);
}

boot();
